/**
 * Fitted Placement
 *
 * Where a freshly added image goes when the customer has not said anything
 * about it yet: fitted into the print area and high on it, the way a chest
 * print sits — not dropped onto the middle of the mockup at whatever size
 * the file happens to have.
 */

export interface PlacementArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FittedPlacement {
  /** Uniform scale for the image. */
  scale: number;
  /** Centre of the image in canvas coordinates. */
  centerX: number;
  centerY: number;
}

/** Share of the print area the image may take up, per axis. */
export const FIT_SHARE = 0.8;
/** The image is centred on this share of the area's height … */
export const FIT_ANCHOR_Y = 1 / 3;
/** … but never closer to the area's top or bottom edge than this share. */
export const FIT_EDGE_MARGIN = 0.05;

/**
 * Fit an image into the print area: as large as possible with neither side
 * above FIT_SHARE of the area (small images are scaled up, large ones down),
 * centred horizontally, centred vertically on the upper third. An image too
 * tall to sit there whole slides down until it clears the top margin.
 */
export function computeFittedPlacement(
  imageWidth: number,
  imageHeight: number,
  area: PlacementArea
): FittedPlacement | null {
  if (
    !(imageWidth > 0) ||
    !(imageHeight > 0) ||
    !(area.width > 0) ||
    !(area.height > 0)
  ) {
    return null;
  }

  const scale = Math.min(
    (area.width * FIT_SHARE) / imageWidth,
    (area.height * FIT_SHARE) / imageHeight
  );
  const half = (imageHeight * scale) / 2;
  const margin = area.height * FIT_EDGE_MARGIN;
  const highest = area.y + margin + half;
  const lowest = area.y + area.height - margin - half;
  const wanted = area.y + area.height * FIT_ANCHOR_Y;

  return {
    scale,
    centerX: area.x + area.width / 2,
    // highest <= lowest always holds: the image is at most 80 % of the
    // height and the margins take 10 %.
    centerY: Math.min(Math.max(wanted, highest), lowest),
  };
}
